import { useCallback, useEffect, useRef, useState } from "react";
import { ApolloClient, useApolloClient } from "@apollo/client";
import { toast } from "@jsq/ds/core";
import {
  ComplianceReportStatus,
  ComplianceReportType,
} from "../../../../graphql/supergraphEnums";
import {
  GpGetComplianceReportDocument,
  useGpCreateScreeningReportMutation,
} from "../../../../graphql/supergraphHooks";
import { getErrorMessage } from "../../../../utils/error_parser";
import type {
  GpGetComplianceReportQuery,
  GpGetComplianceReportQueryVariables,
} from "../../../../graphql/supergraphTypes";

type ComplianceReport = GpGetComplianceReportQuery["getComplianceScreeningReport"];

const ONE_SECOND_MS = 1_000;
const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;

export const SCREENING_REPORT_POLL_INTERVAL_MS = 2 * ONE_SECOND_MS;
export const SCREENING_REPORT_POLL_TIMEOUT_MS = 5 * ONE_MINUTE_MS;

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("aborted", "AbortError"));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
      reject(new DOMException("aborted", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort);
  });

const downloadReport = (url: string) => {
  window.open(url, "_blank");
};

type PollArgs = {
  client: ApolloClient<unknown>;
  arenaId: number;
  reportId: number;
  signal: AbortSignal;
  pollIntervalMs: number;
  pollTimeoutMs: number;
};

const pollUntilDone = async ({
  client,
  arenaId,
  reportId,
  signal,
  pollIntervalMs,
  pollTimeoutMs,
}: PollArgs): Promise<ComplianceReport> => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < pollTimeoutMs) {
    await sleep(pollIntervalMs, signal);
    const { data } = await client.query<
      GpGetComplianceReportQuery,
      GpGetComplianceReportQueryVariables
    >({
      query: GpGetComplianceReportDocument,
      variables: { arenaId, id: reportId },
      fetchPolicy: "no-cache",
      context: { fetchOptions: { signal } },
    });
    const report = data.getComplianceScreeningReport;
    if (report.status === ComplianceReportStatus.Ready) {
      if (report.reportType !== ComplianceReportType.ScreeningStatusReport) {
        throw new Error(
          `Unexpected report type "${report.reportType}" — expected a screening status report.`,
        );
      }
      return report;
    }
    if (report.status === ComplianceReportStatus.Error) {
      throw new Error(report.errorMsg ?? "Report failed to generate");
    }
  }
  throw new Error("Report timed out — try again or contact support");
};

const handleScreeningReportError = (err: unknown) => {
  if (err instanceof DOMException && err.name === "AbortError") return;
  toast.error(getErrorMessage(err));
};

type DownloadArgs = {
  arenaId: number;
  positionIds: number[];
};

type UseScreeningReportDownloadOptions = {
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
};

type UseScreeningReportDownloadResult = {
  downloadScreeningReport: (args: DownloadArgs) => Promise<void>;
  isLoading: boolean;
};

export const useScreeningReportDownload = (
  options: UseScreeningReportDownloadOptions = {},
): UseScreeningReportDownloadResult => {
  const {
    pollIntervalMs = SCREENING_REPORT_POLL_INTERVAL_MS,
    pollTimeoutMs = SCREENING_REPORT_POLL_TIMEOUT_MS,
  } = options;

  const client = useApolloClient();
  const [createScreeningReport] = useGpCreateScreeningReportMutation();
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const downloadScreeningReport = useCallback(
    async ({ arenaId, positionIds }: DownloadArgs) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);

      try {
        const created = await createScreeningReport({
          variables: { arenaId, positionIds },
          fetchPolicy: "no-cache",
          context: { fetchOptions: { signal: controller.signal } },
        });

        if (controller.signal.aborted) return;

        const reportId = created.data?.createComplianceScreeningReport.id;
        if (typeof reportId !== "number") {
          throw new Error("Server did not return a report id");
        }

        const report = await pollUntilDone({
          client,
          arenaId,
          reportId,
          signal: controller.signal,
          pollIntervalMs,
          pollTimeoutMs,
        });

        if (controller.signal.aborted) return;
        if (!report.s3Url) {
          throw new Error("Report is ready but no download URL was provided");
        }
        downloadReport(report.s3Url);
        toast.info("Screening status report exported successfully");
      } catch (err) {
        if (controller.signal.aborted) return;
        handleScreeningReportError(err);
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsLoading(false);
        }
      }
    },
    [client, createScreeningReport, pollIntervalMs, pollTimeoutMs],
  );

  return { downloadScreeningReport, isLoading };
};
